/* ==========================================================================
   Admin Login Page
   Task F20: Admin login with slug-based routing
   ========================================================================== */

import { useState, useEffect } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Checkbox } from '@/components/ui/Checkbox'
import { Alert } from '@/components/ui/Alert'
import { useAdminAuth } from '@/hooks/useAdminAuth'
import { TwoFactorCodeStep } from '@/components/auth/TwoFactorCodeStep'
import { usePortal } from '@/context/PortalContext'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface FormErrors {
  email?: string
  password?: string
}

/* --------------------------------------------------------------------------
   Validation
   -------------------------------------------------------------------------- */

function validateEmail(email: string): string | undefined {
  if (!email.trim()) {
    return 'Email is required'
  }
  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address'
  }
  return undefined
}

function validatePassword(password: string): string | undefined {
  if (!password) {
    return 'Password is required'
  }
  return undefined
}

function validateForm(email: string, password: string): FormErrors {
  return {
    email: validateEmail(email),
    password: validatePassword(password),
  }
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function AdminLogin() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { slug, config, loading: portalLoading } = usePortal()

  // Default redirect to admin dashboard
  const redirectTo = searchParams.get('redirect') || `/rx/${slug}/admin/dashboard`

  const {
    login,
    verifyTwoFactor,
    cancelTwoFactor,
    twoFactorPending,
    loading,
    error: authError,
    clearError,
    isAuthenticated,
  } = useAdminAuth()

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true })
    }
  }, [isAuthenticated, navigate, redirectTo])

  // Form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  // Handle field blur for validation
  const handleBlur = (field: 'email' | 'password') => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    if (field === 'email') {
      setErrors((prev) => ({ ...prev, email: validateEmail(email) }))
    } else {
      setErrors((prev) => ({ ...prev, password: validatePassword(password) }))
    }
  }

  // Clear field error when user starts typing
  const handleEmailChange = (value: string) => {
    setEmail(value)
    clearError()
    if (touched.email) {
      setErrors((prev) => ({ ...prev, email: validateEmail(value) }))
    }
  }

  const handlePasswordChange = (value: string) => {
    setPassword(value)
    clearError()
    if (touched.password) {
      setErrors((prev) => ({ ...prev, password: validatePassword(value) }))
    }
  }

  // Handle form submission
  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Validate all fields
    const formErrors = validateForm(email, password)
    setErrors(formErrors)
    setTouched({ email: true, password: true })

    // Check for validation errors
    if (formErrors.email || formErrors.password) {
      return
    }

    // Attempt login
    const result = await login(email, password, rememberMe)
    if (result.success) {
      navigate(redirectTo, { replace: true })
    }
  }

  // Second step (PORTAL-187): the password was right, now the authenticator code.
  const handleTwoFactorSubmit = async (code: string, rememberDevice: boolean) => {
    const result = await verifyTwoFactor(code, rememberDevice)
    if (result.success) {
      navigate(redirectTo, { replace: true })
    }
  }

  const hasErrors = errors.email || errors.password

  // Show loading while portal config is being fetched
  if (portalLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-beige">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-lavender border-t-deep-purple" />
          <p className="text-sm text-charcoal/70">Loading...</p>
        </div>
      </div>
    )
  }

  if (twoFactorPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <span className="font-serif text-2xl text-deep-purple">
              {config?.pharmacy_name || 'Admin Portal'}
            </span>
          </div>
          <Card className="p-6 sm:p-8">
            <TwoFactorCodeStep
              onSubmit={handleTwoFactorSubmit}
              onCancel={cancelTwoFactor}
              loading={loading}
              error={authError}
            />
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
      <div className="w-full max-w-md">
        {/* Pharmacy Logo/Name */}
        <div className="flex justify-center mb-8">
          <div className="h-15 w-auto flex items-center justify-center">
            {config?.logo_url ? (
              <img
                src={config.logo_url}
                alt={config.pharmacy_name}
                className="h-12 w-auto"
              />
            ) : (
              <span className="font-serif text-2xl text-deep-purple">
                {config?.pharmacy_name || 'Admin Portal'}
              </span>
            )}
          </div>
        </div>

        <Card className="p-6 sm:p-8">
          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="font-serif text-2xl sm:text-3xl text-charcoal">
              Admin Portal
            </h1>
            <p className="text-charcoal/70 mt-2">
              Sign in to manage your pharmacy portal
            </p>
          </div>

          {/* Auth error alert */}
          {authError && (
            <Alert variant="error" className="mb-6">
              {authError}
            </Alert>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-5">
              {/* Email field */}
              <FormField
                label="Email"
                required
                error={touched.email ? errors.email : undefined}
                htmlFor="admin-email"
              >
                <Input
                  id="admin-email"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={() => handleBlur('email')}
                  error={touched.email && !!errors.email}
                  disabled={loading}
                  aria-describedby={errors.email ? 'admin-email-error' : undefined}
                />
              </FormField>

              {/* Password field with show/hide toggle */}
              <FormField
                label="Password"
                required
                error={touched.password ? errors.password : undefined}
                htmlFor="admin-password"
              >
                <div className="relative">
                  <Input
                    id="admin-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    onBlur={() => handleBlur('password')}
                    error={touched.password && !!errors.password}
                    disabled={loading}
                    className="pr-12"
                    aria-describedby={
                      errors.password ? 'admin-password-error' : undefined
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-charcoal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender rounded"
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Eye className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </FormField>

              {/* Remember me checkbox */}
              <Checkbox
                label="Remember me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
              />
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              disabled={loading || !!hasErrors}
              className="w-full mt-6"
            >
              Sign In
            </Button>
          </form>

          {/* Links */}
          <div className="mt-6 space-y-3 text-center">
            <div>
              <Link
                to={`/rx/${slug}/admin/forgot-password`}
                className="text-sm text-deep-purple hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender rounded"
              >
                Forgot password?
              </Link>
            </div>
            <div className="pt-2 border-t border-charcoal/10">
              <p className="text-sm text-charcoal/70">
                Don't have an account?{' '}
                <Link
                  to="/admin/signup"
                  className="text-deep-purple hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender rounded"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </Card>

        {/* Provider login link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-charcoal/70">
            Are you a provider?{' '}
            <Link
              to={`/rx/${slug}/login`}
              className="text-deep-purple hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender rounded"
            >
              Provider login
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default AdminLogin
